import { studentShortLabel } from "@/lib/utils-app";
import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileSignature, CalendarClock, Bot, ClipboardList, Copy, Send, Link2, Loader2, ShieldCheck, FileText } from "lucide-react";
import StudentPicker from "./StudentPicker";

// V1.55 · Generador de documentos en serie — un enlace único con todos los
// documentos que el alumno debe firmar/cumplimentar. Reutiliza los flujos
// públicos existentes (firma, horarios, Kai) + psicotécnico.

const DOC_OPTIONS = [
  { kind: "firma_contrato", label: "Contrato (firma digital)", icon: FileSignature, hint: "Requiere contrato creado sin firmar" },
  { kind: "firma_autorizacion_dgt", label: "Autorización DGT (firma digital)", icon: ShieldCheck, hint: "Requiere autorización DGT creada (módulo 3)" },
  { kind: "firma_autorizacion_menor", label: "Autorización repr. legal — menor (firma)", icon: ShieldCheck, hint: "Requiere autorización de menor creada (módulo 4) — firma el tutor" },
  { kind: "firma_dgt", label: "Modelo DGT Mod.04/07 (firma digital)", icon: FileText, hint: "Requiere Mod.04 o Mod.07 generado en ALICIA (módulo 5)" },
  { kind: "cuestionario_kai", label: "Cuestionario Kai", icon: Bot, hint: "" },
  { kind: "cuestionario_horarios", label: "Disponibilidad de horarios", icon: CalendarClock, hint: "" },
  { kind: "psicotecnico", label: "Solicitud Permiso B (psicotécnico)", icon: ClipboardList, hint: "Se puede enviar solo, si llega días después" },
];

const TTL_OPTIONS = [24, 48, 72];

export default function DocBundleCreator({ student: fixedStudent = null }) {
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [kinds, setKinds] = useState(["firma_contrato", "cuestionario_horarios", "cuestionario_kai"]);
  const [ttl, setTtl] = useState(72);
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (fixedStudent) return;
    (async () => {
      try {
        const { data } = await api.get("/students");
        setStudents((data || []).filter((s) => s.active !== false));
      } catch (err) {
        console.warn("students:", err);
      }
    })();
  }, [fixedStudent]);


  const selected = fixedStudent || students.find((s) => s.id === studentId);

  const toggleKind = (k) =>
    setKinds((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  const create = async () => {
    if (!selected) { toast.error("Selecciona un alumno"); return; }
    if (kinds.length === 0) { toast.error("Selecciona al menos un documento"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/doc-bundles", { student_id: selected.id, kinds, ttl_hours: ttl });
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setCreating(false); }
  };

  const waMessage = result && selected
    ? `Hola ${selected.name.split(" ")[0]} 👋 Soy Kai, el asistente virtual de Natur Autoescuela.\n\nAquí tienes en un único enlace todos los documentos que necesitamos que firmes y completes:\n${result.portal_url}\n\nTe iré guiando paso a paso dentro del enlace. Recuerda que por seguridad caduca en ${ttl} horas — si no te da tiempo, pídenos otro y te lo enviamos inmediatamente. ✅`
    : "";

  const waLink = () => {
    const phone = (selected?.phone || "").replace(/[^\d+]/g, "").replace(/^\+/, "");
    return phone
      ? `https://wa.me/${phone.startsWith("34") ? phone : "34" + phone}?text=${encodeURIComponent(waMessage)}`
      : `https://wa.me/?text=${encodeURIComponent(waMessage)}`;
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(result.portal_url);
    toast.success("Enlace copiado");
  };

  return (
    <div className="card-surface p-4 sm:p-5 max-w-xl" data-testid="doc-bundle-creator">
      <div className="flex items-center gap-2 font-bold mb-4">
        <ClipboardList className="h-5 w-5 text-orange-500" /> Generar enlace de documentos
      </div>

      {!result ? (
          <div className="space-y-4">
            {!fixedStudent && (
            <div>
              <label className="text-xs font-semibold mb-1 block">Alumno *</label>
              {selected ? (
                <div className="flex items-center justify-between rounded-lg border border-emerald-300 bg-emerald-50 px-3 h-10 text-sm" data-testid="bundle-selected-student">
                  <span className="font-semibold">{studentShortLabel(selected)}</span>
                  <button type="button" className="text-xs text-neutral-500 hover:text-red-600" onClick={() => setStudentId("")}>Cambiar</button>
                </div>
              ) : (
                <StudentPicker
                  value={studentId}
                  onChange={setStudentId}
                  students={students}
                  testId="bundle-student-search"
                  optionTestIdPrefix="bundle-student"
                  placeholder="Buscar alumno por nombre o teléfono…"
                  renderMeta={(s) => s.phone || ""}
                  triggerClassName="h-10 rounded-lg"
                />
              )}
            </div>
            )}

            <div>
              <label className="text-xs font-semibold mb-1 block">Documentos a incluir *</label>
              <div className="space-y-1.5">
                {DOC_OPTIONS.map(({ kind, label, icon: Icon, hint }) => (
                  <label key={kind} className={"flex items-start gap-2.5 rounded-lg border p-2.5 cursor-pointer transition-colors " + (kinds.includes(kind) ? "border-orange-300 bg-orange-50" : "border-neutral-200 hover:bg-neutral-50")}>
                    <input type="checkbox" checked={kinds.includes(kind)} onChange={() => toggleKind(kind)}
                      data-testid={`bundle-kind-${kind}`} className="h-4 w-4 mt-0.5 accent-orange-500" />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold flex items-center gap-1.5"><Icon className="h-4 w-4 text-neutral-500" /> {label}</div>
                      {hint && <div className="text-[11px] text-neutral-500">{hint}</div>}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold mb-1 block">Caducidad del enlace</label>
              <div className="inline-flex rounded-lg border border-neutral-200 bg-neutral-50 p-0.5">
                {TTL_OPTIONS.map((h) => (
                  <button key={h} type="button" onClick={() => setTtl(h)} data-testid={`bundle-ttl-${h}`}
                    className={"px-4 h-9 text-sm font-medium rounded " + (ttl === h ? "bg-white shadow text-orange-700" : "text-neutral-600")}>
                    {h} h
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-neutral-500 mt-1">Si el alumno está muy ocupado, dale 72 h para firmar con calma.</p>
            </div>

            <button type="button" onClick={create} disabled={creating} data-testid="bundle-create-btn"
              className="btn-primary w-full !h-11">
              {creating ? <><Loader2 className="h-4 w-4 animate-spin" /> Generando…</> : <><Link2 className="h-4 w-4" /> Generar enlace único</>}
            </button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="bundle-result">
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900">
              Enlace único generado para <strong>{studentShortLabel(selected)}</strong> · caduca en {ttl} h.
            </div>
            <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-2 text-xs font-mono break-all" data-testid="bundle-url">
              {result.portal_url}
            </div>
            <div className="text-xs text-neutral-600 space-y-0.5" data-testid="bundle-items-summary">
              {(result.items || []).map((i) => (
                <div key={i.kind}>
                  • {i.label}{" "}
                  {i.reused && <span className="text-emerald-700 font-semibold">(reutilizado — sin duplicar)</span>}
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={copyLink} data-testid="bundle-copy-btn" className="btn-secondary flex-1">
                <Copy className="h-4 w-4" /> Copiar enlace
              </button>
              <a href={waLink()} target="_blank" rel="noreferrer" data-testid="bundle-whatsapp-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 h-10 rounded-xl text-sm font-semibold bg-green-600 hover:bg-green-700 text-white transition-colors">
                <Send className="h-4 w-4" /> Enviar por WhatsApp
              </a>
            </div>
            <button type="button" onClick={() => { setResult(null); if (!fixedStudent) setStudentId(""); }} data-testid="bundle-reset-btn" className="w-full text-xs text-neutral-500 hover:text-neutral-800 pt-1">Crear otro enlace</button>
          </div>
        )}
    </div>
  );
}
